/**
 * 万年历农历（独立计算，仅用于展示）。
 *
 * 口径说明（与「星雲鑑历」刻意区分，两者必须分开算、分开显示）：
 *   - 万年历农历（本模块）：日月重合时刻落在**北京时间 24:00 之前** → 当天即初一；
 *   - 星雲鑑历（后端 /astrolabe/fateCalculate）：真太阳时 23:00（子时初）之前 → 当天即初一。
 *   两者只在「朔发生在 23:00–24:00」这一极小窗口差一天，所以不能共用一个值。
 *
 * 实现：复用既有依赖 lunar-calendar（表算、东八区、支持 1891–2100），
 * 只做「公历 → 农历文本」，**不写回任何字段、不进请求 payload、不参与任何天文/四柱计算**。
 * 输出格式：`干支年,月名,日名`（半角逗号，如 丙午年,正月初一），与表单展示口径一致。
 *
 * 注意：lunar-calendar 的 GanZhiYear 按「立春」切换，而后端农历的干支年随农历年（正月初一）切换；
 * 本模块用 lunarYear 自行推算干支年，避免立春前后两边显示不一致。
 */
const lunarCalendar = require('lunar-calendar')

const GAN = '甲乙丙丁戊己庚辛壬癸'
const ZHI = '子丑寅卯辰巳午未申酉戌亥'
const MIN_YEAR = 1891
const MAX_YEAR = 2100
/**
 * 分隔符用**半角逗号**：与表单/信息面板的展示口径一致。
 * （后端返回的农历是半角还是全角都用 fateDisplayFormat.formatLunarDateDisplay 统一成半角，
 *   这里直接产出半角，保证「农历」与「星雲鑑历」两行观感一致。）
 */
const LUNAR_SEPARATOR = ','

/** 农历年 → 干支年（1984 = 甲子；以农历年本身为基准，不用立春） */
function toGanZhiYear (lunarYear) {
  // 注意：Number('') === 0、Number(' ') === 0，必须先排除空值，
  // 否则缺年份时会算出「庚申」这类假值
  if (lunarYear === undefined || lunarYear === null) {
    return ''
  }
  const year = Number(String(lunarYear).trim())
  if (!Number.isFinite(year) || year <= 0) {
    return ''
  }
  const offset = (((year - 4) % 60) + 60) % 60
  return `${GAN[offset % 10]}${ZHI[offset % 12]}`
}

/**
 * 解析公历文本，得到 { year, month, day }；解析失败返回 null。
 * 支持 "2026-10-13" / "2026/10/13" / "2026.10.13" / "2026年10月13日"。
 */
function parseSolarDateText (rawText) {
  const text = String(rawText === undefined || rawText === null ? '' : rawText).trim()
  if (!text) {
    return null
  }
  let match = /(-?\d{1,6})\s*[-/.]\s*(\d{1,2})\s*[-/.]\s*(\d{1,2})/.exec(text)
  if (!match) {
    match = /(-?\d{1,6})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?/.exec(text)
  }
  if (!match) {
    return null
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null
  }
  return { year, month, day }
}

/**
 * 公历 → 万年历农历文本。
 *
 * @param {string|Object} value 公历文本（"2026-10-13" / "2026/10/13"）或 { year, month, day }
 * @returns {string} 如 "丙午年，正月初一"；输入无效或超出 1891–2100 时返回 ''
 */
function formatWannianliLunar (value) {
  const parsed = value && typeof value === 'object' ? value : parseSolarDateText(value)
  if (!parsed) {
    return ''
  }
  const year = Number(parsed.year)
  const month = Number(parsed.month)
  const day = Number(parsed.day)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return ''
  }
  // lunar-calendar 仅支持 1891–2100：越界直接留空，由调用方显示"待计算"
  if (year < MIN_YEAR || year > MAX_YEAR) {
    return ''
  }

  const data = lunarCalendar.solarToLunar(year, month, day)
  if (!data || data.error || !data.lunarMonthName || !data.lunarDayName) {
    return ''
  }
  const ganZhiYear = toGanZhiYear(data.lunarYear)
  if (!ganZhiYear) {
    return ''
  }
  // lunarMonthName 自带「闰」前缀（如 闰六月），无需再拼
  return `${ganZhiYear}年${LUNAR_SEPARATOR}${data.lunarMonthName}${LUNAR_SEPARATOR}${data.lunarDayName}`
}

module.exports = {
  MIN_YEAR,
  MAX_YEAR,
  LUNAR_SEPARATOR,
  toGanZhiYear,
  parseSolarDateText,
  formatWannianliLunar
}
